/**
 * ENGG-2438 protects pre-authored content; ENGG-2691 extends deletion and
 * eraser-preview protection to AI work. Movement remains governed separately
 * by isLocked. Agent mutations bypass deletion protection via isAgentActing
 * and bypass shape locks in their editor.run({ ignoreShapeLock: true }) calls.
 */
import type { Editor, TLShape, TLShapeId } from "tldraw";
import { ANNOTATION_META_KEY } from "./annotationContent";
import { isAuthoredShape } from "./authoredContent";

function isTutorShape(shape: TLShape) {
  return isAuthoredShape(shape) || shape.meta.role === "agent";
}

/** `meta.role` values for shapes someone other than the student authored. */
const NON_STUDENT_ROLES: ReadonlySet<unknown> = new Set([
  "agent",
  "spaces-agent",
  "teacher",
]);

/**
 * Work the student owns, whatever its type. Groups carry no meta of their own,
 * so a group of tutor shapes would otherwise pass as the student's.
 */
export function isStudentShape(shape: TLShape) {
  if (shape.type === "group" || isAuthoredShape(shape)) return false;
  return !NON_STUDENT_ROLES.has(shape.meta?.role);
}

export function registerStudentShapeProtection(
  editor: Editor,
  isAgentActing: () => boolean,
) {
  function containsTutorContent(shape: TLShape) {
    if (isTutorShape(shape)) return true;
    let found = false;
    editor.visitDescendants(shape.id, (id) => {
      if (found) return false;
      const descendant = editor.getShape(id);
      if (descendant && isTutorShape(descendant)) found = true;
    });
    return found;
  }

  const cleanupDelete = editor.sideEffects.registerBeforeDeleteHandler(
    "shape",
    (shape) => {
      // A surviving child still needs its ancestors to remain on the page.
      if (!isAgentActing() && containsTutorContent(shape)) return false;
    },
  );
  const cleanupErasing = editor.sideEffects.registerBeforeChangeHandler(
    "instance_page_state",
    (previous, next) => {
      if (previous.erasingShapeIds === next.erasingShapeIds) return next;
      const erasable = new Set<TLShapeId>();
      const collect = (id: TLShapeId) => {
        const shape = editor.getShape(id);
        if (!shape || editor.isShapeOrAncestorLocked(shape)) return;
        if (!containsTutorContent(shape)) {
          erasable.add(id);
          return;
        }
        // Fading a mixed group also fades its AI children. Target only its
        // student branches, leaving protected content at normal opacity.
        for (const childId of editor.getSortedChildIdsForParent(id))
          collect(childId);
      };
      next.erasingShapeIds.forEach(collect);
      return { ...next, erasingShapeIds: [...erasable].sort() };
    },
  );
  const cleanupCreate = editor.sideEffects.registerBeforeCreateHandler(
    "shape",
    (shape) => {
      if (!isTutorShape(shape) || isAgentActing()) return shape;
      // Duplication copies metadata. Student copies need their own authorship
      // and must not participate in the premade fragments' stacking order.
      const {
        source,
        zIndex,
        role,
        [ANNOTATION_META_KEY]: annotation,
        ...restMeta
      } = shape.meta;
      return { ...shape, meta: restMeta, isLocked: false };
    },
  );
  return () => {
    cleanupDelete();
    cleanupErasing();
    cleanupCreate();
  };
}
